import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Pomodoro from "@/components/Pomodoro";
import { subjectStyle, DAYS } from "@/lib/constants";
import {
  CalendarDays, ClipboardList, Target, BookMarked, PencilRuler, ArrowRight,
  Clock, AlertTriangle, Flame, Medal, Plus, Zap, Sparkles,
} from "lucide-react";

function daysUntil(due) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(due + "T00:00:00") - today) / 86400000);
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [gamif, setGamif] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
    api.get("/gamification").then(({ data }) => setGamif(data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="animate-pulse space-y-4" data-testid="dashboard-loading">
        <div className="h-44 bg-slate-200 rounded-3xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-slate-100 rounded-3xl" />)}</div>
      </div>
    );
  }

  const now = new Date();
  const todayName = DAYS[now.getDay() === 0 ? 6 : now.getDay() - 1];
  const dateLabel = now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const firstName = user?.name?.split(" ")[0] || "";
  const qs = data.quick_stats;
  const hwPct = qs.homework_total ? Math.round((qs.homework_done / qs.homework_total) * 100) : 0;

  const overdue = data.homework_pending.filter((h) => daysUntil(h.due_date) < 0).length;
  const urgent = data.homework_pending.filter((h) => daysUntil(h.due_date) <= 1).length;
  const nowTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const nextCourse = data.today_courses.find((c) => c.end_time > nowTime);

  const greeting = now.getHours() < 18 ? "Bonjour" : "Bonsoir";
  const summary =
    overdue > 0
      ? `${overdue} devoir${overdue > 1 ? "s" : ""} en retard — un petit effort et c'est réglé !`
      : urgent > 0
      ? `${urgent} devoir${urgent > 1 ? "s" : ""} pour aujourd'hui ou demain. Tu gères !`
      : data.today_courses.length > 0
      ? `${data.today_courses.length} cours aujourd'hui et aucun devoir urgent. Belle journée en vue !`
      : "Aucun devoir urgent aujourd'hui — parfait pour avancer tes révisions.";

  const STATS = [
    { label: "Devoirs terminés", desc: qs.homework_total ? `${qs.homework_done} sur ${qs.homework_total} au total` : "Ajoute ton premier devoir", value: `${qs.homework_done}/${qs.homework_total}`, pct: hwPct, icon: ClipboardList, color: "bg-rose-100 text-rose-600", testid: "stat-homework" },
    { label: "Exercices réalisés", desc: "chaque exercice te rapporte 15 XP", value: qs.exercises_done, pct: Math.min(100, qs.exercises_done * 10), icon: PencilRuler, color: "bg-sky-100 text-sky-600", testid: "stat-exercises" },
    { label: "Fiches créées", desc: "ta bibliothèque perso de révision", value: qs.sheets_count, pct: Math.min(100, qs.sheets_count * 10), icon: BookMarked, color: "bg-emerald-100 text-emerald-600", testid: "stat-sheets" },
    { label: "Devoirs dans les temps", desc: "ton taux de réussite", value: `${hwPct}%`, pct: hwPct, icon: Target, color: "bg-amber-100 text-amber-600", testid: "stat-homework-pct" },
  ];

  const QUICK_ACTIONS = [
    { to: "/homework", icon: Plus, label: "Ajouter un devoir", testid: "quick-add-homework" },
    { to: "/revisions", icon: BookMarked, label: "Créer une fiche", testid: "quick-add-sheet" },
    { to: "/goals", icon: Target, label: "Nouvel objectif", testid: "quick-add-goal" },
    { to: "/flashcards", icon: Zap, label: "Réviser mes cartes", testid: "quick-flashcards" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* HERO */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-600 p-6 sm:p-8 text-white shadow-lg animate-fade-in-up" data-testid="dashboard-hero">
        <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10" />
        <div className="absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-white/5" />
        <div className="relative">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/15 text-xs font-semibold capitalize" data-testid="dashboard-date-badge">
              <CalendarDays className="h-3.5 w-3.5" /> {dateLabel}
            </span>
            {gamif && (
              <Link to="/progress" className="inline-flex items-center gap-3 px-3.5 py-1.5 rounded-full bg-white/15 text-xs font-semibold hover:bg-white/25 transition-colors duration-200" data-testid="dashboard-gamif-pill">
                <span className="flex items-center gap-1"><Flame className="h-3.5 w-3.5 text-amber-300" /> {gamif.streak} j</span>
                <span className="h-3 w-px bg-white/30" />
                <span className="flex items-center gap-1"><Medal className="h-3.5 w-3.5" /> Niv. {gamif.level}</span>
                <span className="hidden sm:block w-16 h-1.5 rounded-full bg-white/25 overflow-hidden">
                  <span className="block h-full bg-amber-300 rounded-full" style={{ width: `${gamif.progress}%` }} />
                </span>
              </Link>
            )}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight" data-testid="dashboard-greeting">
            {greeting} {firstName} !
          </h1>
          <p className="mt-2 text-sm sm:text-base text-indigo-100 max-w-xl" data-testid="dashboard-summary">
            {summary}
          </p>
          <div className="mt-5 flex flex-wrap gap-2.5">
            {QUICK_ACTIONS.map(({ to, icon: Icon, label, testid }) => (
              <Link
                key={testid}
                to={to}
                data-testid={testid}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/15 hover:bg-white/25 text-sm font-semibold transition-colors duration-200 backdrop-blur-sm"
              >
                <Icon className="h-4 w-4" /> {label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {STATS.map(({ label, desc, value, pct, icon: Icon, color, testid }) => (
          <div key={testid} data-testid={testid} className="bg-white rounded-3xl border border-slate-200/80 p-5 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
            <div className={`h-9 w-9 rounded-xl ${color} flex items-center justify-center mb-3`}>
              <Icon className="h-[18px] w-[18px]" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            <p className="text-xs font-semibold text-slate-700 mt-0.5">{label}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">{desc}</p>
            <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        ))}
      </div>

      <Pomodoro />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
        {/* Cours du jour */}
        <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="dashboard-today-courses">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-indigo-600" /> Cours aujourd'hui
            </h2>
            <Link to="/timetable" data-testid="dashboard-goto-timetable" className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
              Tout voir <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <p className="text-xs text-slate-400 mb-4">Ta journée, heure par heure.</p>
          {data.today_courses.length === 0 ? (
            <div className="text-center py-8">
              <Sparkles className="h-8 w-8 text-indigo-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500 font-medium">Pas de cours aujourd'hui !</p>
              <p className="text-xs text-slate-400 mt-1">Le moment idéal pour une session de révision.</p>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {data.today_courses.map((c) => {
                const isNext = nextCourse?.id === c.id;
                return (
                  <li key={c.id} data-testid={`dashboard-course-${c.id}`} className={`relative flex items-center gap-3 px-3.5 py-2.5 rounded-xl border ${subjectStyle(c.subject)} ${isNext ? "ring-2 ring-indigo-400/60" : ""}`}>
                    <div className="text-xs font-bold shrink-0 w-20">
                      {c.start_time}<br />{c.end_time}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{c.subject}</p>
                      <p className="text-xs opacity-75 truncate">{[c.room, c.teacher].filter(Boolean).join(" · ") || "—"}</p>
                    </div>
                    {isNext && (
                      <span className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-indigo-600 text-white text-[9px] font-bold uppercase tracking-wider" data-testid="dashboard-next-course">
                        Prochain cours
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Devoirs urgents */}
        <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="dashboard-pending-homework">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-rose-500" /> Devoirs à faire
            </h2>
            <Link to="/homework" data-testid="dashboard-goto-homework" className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
              Tout voir <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <p className="text-xs text-slate-400 mb-4">Par ordre d'urgence — commence par le premier !</p>
          {data.homework_pending.length === 0 ? (
            <div className="text-center py-8">
              <Target className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-slate-500 font-medium">Aucun devoir en attente. Bravo !</p>
              <p className="text-xs text-slate-400 mt-1">Profites-en pour créer une fiche ou faire un exercice.</p>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {data.homework_pending.map((h) => {
                const d = daysUntil(h.due_date);
                return (
                  <li key={h.id} data-testid={`dashboard-homework-${h.id}`} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-100">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full border shrink-0 ${subjectStyle(h.subject)}`}>{h.subject}</span>
                    <p className="text-sm font-medium text-slate-800 truncate flex-1">{h.title}</p>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full shrink-0 flex items-center gap-1 ${
                      d < 0 ? "bg-rose-100 text-rose-700" : d <= 1 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                    }`}>
                      {d <= 1 && <AlertTriangle className="h-3 w-3" />}
                      {d < 0 ? "En retard" : d === 0 ? "Aujourd'hui" : d === 1 ? "Demain" : `J-${d}`}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Objectifs du mois */}
        <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="dashboard-goals">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Target className="h-5 w-5 text-amber-500" /> Objectifs du mois
            </h2>
            <Link to="/goals" data-testid="dashboard-goto-goals" className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
              Tout voir <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <p className="text-xs text-slate-400 mb-4">Tes défis du mois — chaque progrès compte.</p>
          {data.goals.length === 0 ? (
            <div className="text-center py-8">
              <Target className="h-8 w-8 text-amber-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500 font-medium">Pas encore d'objectif ce mois-ci.</p>
              <p className="text-xs text-slate-400 mt-1">Fixe-toi un défi : « 10 fiches », « 15 exercices »…</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {data.goals.slice(0, 4).map((g) => {
                const pct = Math.min(100, Math.round((g.current / g.target) * 100));
                return (
                  <li key={g.id} data-testid={`dashboard-goal-${g.id}`}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="font-medium text-slate-800 truncate">{g.title}</span>
                      <span className="text-slate-500 text-xs font-semibold shrink-0 ml-2">{g.current}/{g.target}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {pct >= 100 ? "Objectif accompli, bravo !" : pct >= 60 ? "Tu y es presque, continue !" : pct > 0 ? "Bon début, garde le rythme !" : "C'est parti !"}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Fiches récentes */}
      <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="dashboard-recent-sheets">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <BookMarked className="h-5 w-5 text-emerald-500" /> Mes dernières fiches
          </h2>
          <Link to="/revisions" data-testid="dashboard-goto-revisions" className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
            Tout voir <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <p className="text-xs text-slate-400 mb-4">Reprends là où tu t'étais arrêté.</p>
        {data.recent_sheets.length === 0 ? (
          <div className="text-center py-8">
            <BookMarked className="h-8 w-8 text-emerald-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500 font-medium">Aucune fiche pour l'instant.</p>
            <p className="text-xs text-slate-400 mt-1">Crée ta première fiche ou pioche dans la bibliothèque officielle !</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.recent_sheets.map((s) => (
              <Link
                key={s.id}
                to="/revisions"
                data-testid={`dashboard-sheet-${s.id}`}
                className="rounded-2xl border border-slate-200/80 p-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 bg-slate-50/50"
              >
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${subjectStyle(s.subject)}`}>{s.subject}</span>
                <p className="mt-2.5 text-sm font-semibold text-slate-900 line-clamp-2">{s.title}</p>
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {s.chapter} · ≈ {Math.max(2, Math.round(s.content.length / 800))} min de lecture
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
